import { Button, Menu, Stack } from "@mantine/core";
import { ActionCard } from "../../../components/ActionCard";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { useTRPC } from "../../../trpc";
import { useDisclosure } from "@mantine/hooks";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { AgGridTable } from "../../../components/AgGridTable.js";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import type { RouterOutputs } from "@project/api";
import { UserCellRenderer } from "../../../utils/columns";
import { IconRestore, IconLockAccessOff } from "@tabler/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import { assertAccountSelected, useUser } from "../../../hooks/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { InviteMembersModal } from "./components/InviteMembersModal.js";
import { ErrorAlert } from "../components/ErrorAlert.js";
import { LimitExceeded } from "../components/LimitExceeded.js";
import { useMapkaNotification } from "../../../hooks/notifications.js";
import { TieredPriceProductId } from "@project/common";
import { useProductNames } from "../components/hooks/productLabels.js";

type User = RouterOutputs["getAccountUsers"][number];

export const AccountMembers = () => {
  const intl = useIntl();
  const user = useUser();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  assertAccountSelected(user);

  const { data = [] } = useQuery(trpc.getAccountUsers.queryOptions());
  const { mutate: deleteUser } = useMutation(trpc.deactivateUserForCurrentAccount.mutationOptions());
  const { mutate: restoreUser } = useMutation(trpc.reactivateUserForCurrentAccount.mutationOptions());
  const [errorMessage, setErrorMessage] = useState<string>();
  const mapkaNotification = useMapkaNotification();

  const {
    currentAccount,
  } = user;

  const [
    opened,
    {
      open,
      close,
    },
  ] = useDisclosure(false);

  const handleDeactivate = useCallback((id: number, name: string) => {
    deleteUser({ id }, {
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.getAccountUsers.queryFilter());
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "User deactivated",
            id: "notification.userDeactivated",
          }),
          message: intl.formatMessage({
            defaultMessage: "Successfully deactivated access for: {name}",
            id: "notification.userDeactivatedMessage",
          }, { name }),
          variant: "success",
        });
      },
    });
  }, [deleteUser, intl]);

  const handleActivate = useCallback((id: number, name: string) => {
    restoreUser({ id }, {

      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.getAccountUsers.queryFilter());
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "User reactivated",
            id: "notification.userReactivated",
          }),
          message: intl.formatMessage({
            defaultMessage: "Successfully reactivated access for: {name}",
            id: "notification.userReactivatedMessage",
          }, { name }),
          variant: "success",
        });
      },

      onError: ({ message }) => {
        if (message.includes("User limit exceeded")) {
          mapkaNotification({
            title: intl.formatMessage({
              defaultMessage: "Members limit exceeded",
              id: "notification.membersLimitExceeded",
            }),
            message: intl.formatMessage({
              defaultMessage: "Hey, you've reached the maximum number of members for this account. Please upgrade your subscription.",
              id: "notification.membersLimitExceededMessage",
            }),
            variant: "warning",
          });
        }
      },
    });
  }, [restoreUser, intl]);

  const ActionsCellRenderer = ({ data: rowData }: ICellRendererParams<User>) => {
    if (!rowData) return null;
    return (
      <Menu shadow="md">
        <Menu.Target>
          <Button variant="outline" size="xs">
            Actions
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {!rowData.deletedAt && (
            <Menu.Item
              leftSection={<IconLockAccessOff />}
              onClick={() => {
                handleDeactivate(rowData.id, rowData.name);
              }}
            >
              <FormattedMessage defaultMessage="Deactivate" id="button.deactivate" />
            </Menu.Item>
          )}
          {rowData.deletedAt && (
            <Menu.Item
              leftSection={<IconRestore />}
              onClick={() => {
                handleActivate(rowData.id, rowData.name);
              }}
            >
              <FormattedMessage defaultMessage="Reactivate" id="button.reactivate" />
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    );
  };

  const columnDefs: ColDef<User>[] = useMemo(() => [
    {
      field: "name",
      headerName: intl.formatMessage({ defaultMessage: "User", id: "column.user" }),
      cellRenderer: UserCellRenderer,
    },
    {
      field: "email",
      headerName: intl.formatMessage({ defaultMessage: "Email", id: "column.email" }),
    },
    {
      field: "deletedAt",
      headerName: intl.formatMessage({ defaultMessage: "Status", id: "column.status" }),
      valueGetter: params => params.data?.deletedAt
        ? "inactive"
        : "active",
    },
    {
      field: "createdAt",
      headerName: intl.formatMessage({ defaultMessage: "Created at", id: "column.createdAt" }),
      sortable: true,
      cellDataType: "dateTimeString",
    },
    {
      headerName: "Actions",
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      filter: false,
    },
  ], [intl, handleDeactivate, handleActivate]);

  let errorMessageNode: ReactNode = null;
  const getName = useProductNames();

  if (errorMessage) {
    if (errorMessage.includes("User limit exceeded")) {
      errorMessageNode = (
        <ErrorAlert>
          <LimitExceeded
            limitOfWhatExceeded={getName(TieredPriceProductId.TieredAccountUser)}
          />
        </ErrorAlert>
      );
    }
  }

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Manage account", id: "navigation.manageAccount" })}
        current={intl.formatMessage({ defaultMessage: "Members", id: "navigation.members" })}
        description={intl.formatMessage({
          defaultMessage: "Add new account members, edit or revoke permissions and access, and resend verifications emails.",
          id: "text.membersDescription",
        })}
      />

      <ActionCard
        title={`Invite Members to join ${currentAccount.name} account`}
        subTitle="Invite members to join your account and collaborate on your projects"
      >
        <Button onClick={open} variant="primary">
          <FormattedMessage defaultMessage="Invite members" id="button.inviteMembers" />
        </Button>
      </ActionCard>

      {errorMessageNode}

      <AgGridTable
        rowData={data}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        autoSizeStrategy={{
          type: "fitGridWidth",
          defaultMinWidth: 100,
        }}
        suppressHorizontalScroll={false}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true,
        }}
      />

      <InviteMembersModal opened={opened} close={close} onError={setErrorMessage} />
    </Stack>
  );
};
